import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, RefreshCw, ExternalLink, Star, ChevronUp, ChevronDown } from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { ListSkeleton } from '../components/ui';

/* ── costanti ──────────────────────────────────────────────────────── */
const STATI = ['nuovo','contattato','in_trattativa','won','lost'];
const STATO_STYLE = {
  nuovo:         { bg:'var(--surface)',     color:'var(--text-muted)',  label:'Nuovo' },
  contattato:    { bg:'var(--cyan-dim)',    color:'var(--cyan)',        label:'Contattato' },
  in_trattativa: { bg:'var(--yellow-dim)', color:'var(--yellow)',      label:'In trattativa' },
  won:           { bg:'var(--green-dim)',   color:'var(--green)',       label:'Won ✓' },
  lost:          { bg:'var(--red-dim)',     color:'var(--red)',         label:'Lost' },
};
const EMPTY = { nome:'', azienda:'', email:'', telefono:'', fonte:'', stato:'nuovo', note:'', assegnato_a:'' };

/* ── piccoli componenti ─────────────────────────────────────────────── */
function StatoSelect({ stato, onChange }) {
  const st = STATO_STYLE[stato] || STATO_STYLE.nuovo;
  return (
    <select value={stato || 'nuovo'} onChange={e => onChange(e.target.value)}
      style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:11,
        fontWeight:600, padding:0, outline:'none', color: st.color }}>
      {STATI.map(s => (
        <option key={s} value={s} style={{ background:'var(--bg2)', color:'var(--text)' }}>
          {STATO_STYLE[s].label}
        </option>
      ))}
    </select>
  );
}

function RatingStars({ rating }) {
  if (!rating) return null;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:11.5, color:'var(--yellow)' }}>
      <Star size={11} fill="var(--yellow)" strokeWidth={0}/>{rating.toFixed(1)}
    </span>
  );
}

function FiltriStato({ leads, filtro, onChange }) {
  return (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
      <button onClick={() => onChange('')}
        style={{ fontSize:11.5, padding:'4px 11px', borderRadius:7, border:'1px solid', cursor:'pointer',
          borderColor: !filtro ? 'rgba(0,212,255,0.4)' : 'var(--border)',
          background: !filtro ? 'var(--cyan-dim)' : 'var(--surface)',
          color: !filtro ? 'var(--cyan)' : 'var(--text-muted)' }}>
        Tutti ({leads.length})
      </button>
      {STATI.map(s => {
        const cnt = leads.filter(l => l.stato === s).length;
        if (!cnt) return null;
        const st = STATO_STYLE[s];
        return (
          <button key={s} onClick={() => onChange(filtro === s ? '' : s)}
            style={{ fontSize:11.5, padding:'4px 11px', borderRadius:7, border:'1px solid', cursor:'pointer',
              borderColor: filtro===s ? st.color+'99' : 'var(--border)',
              background: filtro===s ? st.bg : 'var(--surface)',
              color: filtro===s ? st.color : 'var(--text-muted)' }}>
            {st.label} ({cnt})
          </button>
        );
      })}
    </div>
  );
}

/* ── LH status config ───────────────────────────────────────────────── */
const LH_STATI = [
  { v:'nuovo',      label:'Nuovo',      color:'#3b82f6' },
  { v:'contattato', label:'Contattato', color:'#8b5cf6' },
  { v:'trattativa', label:'Trattativa', color:'#f59e0b' },
  { v:'cliente',    label:'Cliente',    color:'#10b981' },
  { v:'perso',      label:'Perso',      color:'#ef4444' },
];
const lhStatoColor = v => (LH_STATI.find(s => s.v === v) || LH_STATI[0]).color;

const WEB_BADGE = {
  social:   { label:'Social',   bg:'rgba(251,146,60,0.15)', color:'#fb923c' },
  website:  { label:'Sito web', bg:'rgba(34,197,94,0.15)',  color:'#22c55e' },
  ecommerce:{ label:'E-comm',   bg:'rgba(168,85,247,0.15)', color:'#a855f7' },
};

/* ── singola riga LH ────────────────────────────────────────────────── */
function LHDbRow({ lead, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes]       = useState(lead.notes || '');
  const [events, setEvents]     = useState(null); // null = non caricati
  const hasBrief = lead.brief_id || lead.brief_status === 'brief_generato';
  const web = WEB_BADGE[lead.website_type] || null;

  const toggleExpand = async () => {
    if (!expanded && events === null) {
      try {
        const r = await api.get(`/leads/lh_lead/${lead.id}/events`);
        setEvents(r.data);
      } catch { setEvents([]); }
    }
    setExpanded(e => !e);
  };

  const saveNotes = async () => {
    try { await api.patch(`/leads/lh_lead/${lead.id}/notes`, { notes }); } catch {}
  };

  const changeStatus = async (v) => {
    try { await api.patch(`/leads/lh_lead/${lead.id}/status`, { status: v }); onStatusChange(lead.id, v); } catch {}
  };

  return (
    <>
      {/* riga principale */}
      <div onClick={toggleExpand} className="eden-grid-row" style={{
        display:'grid',
        gridTemplateColumns:'2fr 1.1fr 1.1fr 80px 72px 110px 110px 44px',
        alignItems:'center', padding:'10px 14px',
        borderBottom:'1px solid var(--border)',
        cursor:'pointer',
        background: expanded ? 'rgba(255,255,255,0.03)' : 'transparent',
        transition:'background 0.12s',
        minWidth:760,
      }}>
        {/* Azienda */}
        <div style={{ minWidth:0, paddingRight:8 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {lead.name}
          </div>
          {lead.city && <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:1 }}>{lead.city}</div>}
        </div>

        {/* Categoria */}
        <span style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', paddingRight:6 }}>
          {lead.category || '—'}
        </span>

        {/* Telefono */}
        <span style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {lead.phone || '—'}
        </span>

        {/* Rating */}
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          {lead.rating ? (
            <>
              <Star size={12} fill="var(--yellow)" strokeWidth={0} style={{ color:'var(--yellow)', flexShrink:0 }}/>
              <span style={{ fontSize:12, fontWeight:600, color:'var(--yellow)' }}>{Number(lead.rating).toFixed(1)}</span>
              {lead.reviews_count > 0 && <span style={{ fontSize:10, color:'var(--text-dim)' }}>({lead.reviews_count})</span>}
            </>
          ) : <span style={{ fontSize:11, color:'var(--text-dim)' }}>N/D</span>}
        </div>

        {/* Web badge */}
        <div>
          {web ? (
            <a href={lead.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
              style={{ textDecoration:'none', display:'inline-flex', alignItems:'center', gap:3,
                fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:5,
                background: web.bg, color: web.color }}>
              {web.label} <ExternalLink size={9}/>
            </a>
          ) : <span style={{ fontSize:11, color:'var(--text-dim)' }}>—</span>}
        </div>

        {/* Stato */}
        <div onClick={e => e.stopPropagation()}>
          <select value={lead.status || 'nuovo'} onChange={e => changeStatus(e.target.value)}
            style={{
              fontSize:11.5, fontWeight:600, padding:'3px 7px', borderRadius:6, cursor:'pointer',
              border:`1px solid ${lhStatoColor(lead.status)}55`,
              background:`${lhStatoColor(lead.status)}18`,
              color: lhStatoColor(lead.status),
              outline:'none', appearance:'none', width:'100%',
            }}>
            {LH_STATI.map(s => (
              <option key={s.v} value={s.v} style={{ background:'var(--bg2)', color:'var(--text)' }}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Brief */}
        <div onClick={e => e.stopPropagation()} style={{ display:'flex', justifyContent:'center' }}>
          {hasBrief && (
            <a href={`https://edenhunter.pezcorp.it`} target="_blank" rel="noreferrer"
              style={{ fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:6,
                background:'rgba(0,212,255,0.15)', color:'var(--cyan)', textDecoration:'none',
                border:'1px solid rgba(0,212,255,0.25)', whiteSpace:'nowrap' }}>
              Apri brief
            </a>
          )}
        </div>

        {/* Maps */}
        <div style={{ display:'flex', justifyContent:'center' }} onClick={e => e.stopPropagation()}>
          {lead.google_maps_url
            ? <a href={lead.google_maps_url} target="_blank" rel="noreferrer"
                style={{ color:'var(--text-dim)', display:'flex', alignItems:'center' }}
                title="Google Maps">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
              </a>
            : <span style={{ color:'var(--text-dim)', fontSize:11 }}>—</span>}
        </div>
      </div>

      {/* Pannello espanso: note + eventi */}
      {expanded && (
        <div style={{
          display:'grid', gridTemplateColumns:'1fr 1fr', gap:0,
          borderBottom:'1px solid var(--border)',
          background:'rgba(255,255,255,0.02)',
          minWidth:820,
        }}>
          {/* Note */}
          <div style={{ padding:'12px 16px 14px', borderRight:'1px solid var(--border)' }}>
            <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-dim)', marginBottom:6 }}>Note</div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Note sull'interazione…"
              style={{ width:'100%', minHeight:70, background:'rgba(255,255,255,0.04)', border:'1px solid var(--border)',
                borderRadius:8, color:'var(--text)', fontSize:12.5, padding:'8px 10px', resize:'vertical',
                outline:'none', fontFamily:'inherit', lineHeight:1.5 }}
            />
          </div>

          {/* Eventi */}
          <div style={{ padding:'12px 16px 14px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <span style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-dim)' }}>
                Eventi collegati
              </span>
              <a href="https://edenhunter.pezcorp.it" target="_blank" rel="noreferrer"
                style={{ fontSize:11, color:'var(--cyan)', textDecoration:'none', display:'flex', alignItems:'center', gap:3 }}>
                + Aggiungi <ExternalLink size={9}/>
              </a>
            </div>
            {events === null ? (
              <span style={{ fontSize:12, color:'var(--text-dim)' }}>Caricamento…</span>
            ) : events.length === 0 ? (
              <span style={{ fontSize:12, color:'var(--text-dim)' }}>Nessun evento</span>
            ) : events.map(ev => (
              <div key={ev.id} style={{ fontSize:12, color: ev.done ? 'var(--text-dim)' : 'var(--text-muted)',
                padding:'4px 0', borderBottom:'1px solid var(--border)', display:'flex', gap:8, alignItems:'center' }}>
                {ev.done
                  ? <span style={{ color:'var(--green)', fontSize:10 }}>✓</span>
                  : <span style={{ color:'var(--yellow)', fontSize:10 }}>●</span>}
                <span style={{ textDecoration: ev.done ? 'line-through' : 'none' }}>{ev.title}</span>
                <span style={{ color:'var(--text-dim)', fontSize:10.5, marginLeft:'auto' }}>{ev.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ── TAB: Lead Hunter (replica nativa DB view) ──────────────────────── */
function TabLH({ lhStats }) {
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filtroStato, setFiltroStato] = useState('');

  useEffect(() => {
    api.get('/leads/lh_database')
      .then(r => { setLeads(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleStatusChange = (lhId, newStatus) => {
    setLeads(prev => prev.map(l => l.id === lhId ? { ...l, status: newStatus } : l));
  };

  let filtered = leads;
  if (filtroStato) filtered = filtered.filter(l => (l.status || 'nuovo') === filtroStato);
  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter(l =>
      (l.name||'').toLowerCase().includes(q) ||
      (l.category||'').toLowerCase().includes(q) ||
      (l.city||'').toLowerCase().includes(q) ||
      (l.phone||'').includes(q)
    );
  }

  const COLS = ['Azienda','Categoria','Telefono','Rating','Web','Stato','Brief',''];

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, flexWrap:'wrap' }}>
        {/* Search */}
        <input
          placeholder="Cerca azienda, categoria, città…"
          value={search} onChange={e => setSearch(e.target.value)}
          className="eden-input"
          style={{ maxWidth:280, minHeight:'unset', padding:'7px 12px', fontSize:12.5 }}
        />
        {/* Filtri stato */}
        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
          <button onClick={() => setFiltroStato('')}
            style={{ fontSize:11.5, padding:'4px 10px', borderRadius:6, border:'1px solid', cursor:'pointer',
              borderColor: !filtroStato ? 'rgba(0,212,255,0.4)' : 'var(--border)',
              background: !filtroStato ? 'var(--cyan-dim)' : 'var(--surface)',
              color: !filtroStato ? 'var(--cyan)' : 'var(--text-muted)' }}>
            Tutti ({leads.length})
          </button>
          {LH_STATI.map(s => {
            const cnt = leads.filter(l => (l.status || 'nuovo') === s.v).length;
            if (!cnt) return null;
            const active = filtroStato === s.v;
            return (
              <button key={s.v} onClick={() => setFiltroStato(active ? '' : s.v)}
                style={{ fontSize:11.5, padding:'4px 10px', borderRadius:6, border:'1px solid', cursor:'pointer',
                  borderColor: active ? s.color+'88' : 'var(--border)',
                  background: active ? s.color+'18' : 'var(--surface)',
                  color: active ? s.color : 'var(--text-muted)' }}>
                {s.label} ({cnt})
              </button>
            );
          })}
        </div>
        <span style={{ fontSize:12, color:'var(--text-dim)', marginLeft:'auto' }}>
          {filtered.length} lead
        </span>
        <a href="https://edenhunter.pezcorp.it" target="_blank" rel="noreferrer"
          style={{ fontSize:11.5, color:'var(--text-dim)', display:'flex', alignItems:'center', gap:4, textDecoration:'none' }}>
          <ExternalLink size={12}/> Apri LH
        </a>
      </div>

      {/* Tabella */}
      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          <RefreshCw size={15} style={{ animation:'spin 1s linear infinite' }}/> Caricamento…
        </div>
      ) : (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            {/* Header */}
            <div className="eden-grid-head" style={{ display:'grid', gridTemplateColumns:'2fr 1.1fr 1.1fr 80px 72px 110px 110px 44px',
              padding:'9px 14px', background:'var(--bg3)', borderBottom:'1px solid var(--border)', minWidth:760 }}>
              {COLS.map((h, i) => (
                <span key={i} style={{ fontSize:10.5, color:'var(--text-muted)', fontWeight:700,
                  textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</span>
              ))}
            </div>
            {/* Righe */}
            {filtered.length === 0 ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Nessun lead trovato</div>
            ) : filtered.map(l => (
              <LHDbRow key={l.id} lead={l} onStatusChange={handleStatusChange}/>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── TAB: Direttori ─────────────────────────────────────────────────── */
function FilterChips({ label, items, active, onToggle, color = 'var(--cyan)', colorDim = 'rgba(0,212,255,0.4)' }) {
  return (
    <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center', marginBottom:8 }}>
      <span style={{ fontSize:10, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em', alignSelf:'center', marginRight:2 }}>{label}</span>
      <button onClick={() => onToggle('')}
        style={{ fontSize:11.5, padding:'3px 10px', borderRadius:6, border:'1px solid', cursor:'pointer',
          borderColor: !active ? colorDim : 'var(--border)',
          background: !active ? `${color}18` : 'var(--surface)',
          color: !active ? color : 'var(--text-muted)' }}>
        Tutti
      </button>
      {items.map(item => (
        <button key={item} onClick={() => onToggle(active === item ? '' : item)}
          style={{ fontSize:11.5, padding:'3px 10px', borderRadius:6, border:'1px solid', cursor:'pointer',
            borderColor: active===item ? colorDim : 'var(--border)',
            background: active===item ? `${color}18` : 'var(--surface)',
            color: active===item ? color : 'var(--text-muted)' }}>
          {item}
        </button>
      ))}
    </div>
  );
}

function TabDirettori({ leads, onStatoChange, onEdit, onDelete }) {
  const [filtroStato, setFiltroStato]           = useState('');
  const [filtroCliente, setFiltroCliente]       = useState('');
  const [filtroDirettore, setFiltroDirettore]   = useState('');
  const [sortRating, setSortRating]             = useState('desc'); // 'desc' | 'asc' | null

  const direttori = [...new Set(leads.map(l => l.direttore).filter(Boolean))].sort();
  const clienti   = [...new Set(leads.map(l => l.cliente_b2b).filter(Boolean))].sort();

  let filtered = leads;
  if (filtroStato)     filtered = filtered.filter(l => l.stato === filtroStato);
  if (filtroCliente)   filtered = filtered.filter(l => l.cliente_b2b === filtroCliente);
  if (filtroDirettore) filtered = filtered.filter(l => l.direttore === filtroDirettore);

  if (sortRating === 'desc') filtered = [...filtered].sort((a,b) => (b.rating_val||0) - (a.rating_val||0));
  if (sortRating === 'asc')  filtered = [...filtered].sort((a,b) => (a.rating_val||0) - (b.rating_val||0));

  const cycleSort = () => setSortRating(s => s === 'desc' ? 'asc' : s === 'asc' ? null : 'desc');

  return (
    <div>
      <p style={{ margin:'0 0 12px', fontSize:13, color:'var(--text-muted)' }}>
        Lead legati a <b style={{ color:'var(--cyan)' }}>Direttori B2B</b> — distribuzione per territorio e cliente
      </p>

      {/* Filtri stato */}
      <FiltriStato leads={leads} filtro={filtroStato} onChange={setFiltroStato}/>

      {/* Filtri cliente B2B */}
      {clienti.length > 0 && (
        <FilterChips
          label="Cliente B2B" items={clienti} active={filtroCliente}
          onToggle={setFiltroCliente}
          color="var(--purple)" colorDim="rgba(168,85,247,0.35)"
        />
      )}

      {/* Filtri direttore */}
      {direttori.length > 1 && (
        <FilterChips
          label="Direttore" items={direttori} active={filtroDirettore}
          onToggle={setFiltroDirettore}
          color="var(--cyan)" colorDim="rgba(0,212,255,0.35)"
        />
      )}

      <div style={{ marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:12, color:'var(--text-dim)' }}>{filtered.length} lead</span>
        <button onClick={cycleSort}
          style={{ display:'flex', alignItems:'center', gap:4, fontSize:11.5, padding:'4px 10px',
            background: sortRating ? 'var(--yellow-dim)' : 'var(--surface)',
            border:`1px solid ${sortRating ? 'rgba(245,158,11,0.3)' : 'var(--border)'}`,
            borderRadius:6, color: sortRating ? 'var(--yellow)' : 'var(--text-muted)', cursor:'pointer', fontWeight:500 }}>
          {sortRating === 'desc' ? <ChevronDown size={12}/> : sortRating === 'asc' ? <ChevronUp size={12}/> : <span style={{ fontSize:10 }}>—</span>}
          Rating
        </button>
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:14 }}>Nessun lead</div>
      ) : (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <div className="eden-grid-head" style={{ display:'grid', gridTemplateColumns:'1.6fr 1fr 1fr 0.9fr 0.9fr 80px',
              padding:'9px 16px', background:'var(--bg3)', borderBottom:'1px solid var(--border)', minWidth:600 }}>
              {['Azienda / Città','Direttore','Cliente B2B','Rating','Stato',''].map((h,i) => (
                <span key={i} style={{ fontSize:10.5, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</span>
              ))}
            </div>
            {filtered.map(l => (
              <DirettoreRow key={l.id} lead={l} onStatoChange={onStatoChange} onEdit={onEdit} onDelete={onDelete}/>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DirettoreRow({ lead, onStatoChange, onEdit, onDelete }) {
  const rating = lead.rating_val || 0;
  return (
    <div className="eden-grid-row" style={{ display:'grid', gridTemplateColumns:'1.6fr 1fr 1fr 0.9fr 0.9fr 80px',
      alignItems:'center', padding:'10px 16px', borderBottom:'1px solid var(--border)', minWidth:600 }}>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {lead.azienda || lead.nome}
        </div>
        {lead.citta && <div style={{ fontSize:11, color:'var(--text-dim)' }}>{lead.citta}{lead.zona ? ` · ${lead.zona}` : ''}</div>}
      </div>
      <span style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {lead.direttore || '—'}
      </span>
      {/* Cliente B2B con badge colorato */}
      <span style={{ fontSize:11.5 }}>
        {lead.cliente_b2b
          ? <span style={{ padding:'2px 8px', borderRadius:5, fontSize:11, fontWeight:600,
              background:'rgba(168,85,247,0.12)', color:'var(--purple)' }}>{lead.cliente_b2b}</span>
          : <span style={{ color:'var(--text-dim)' }}>—</span>}
      </span>
      {/* Rating con barre visive */}
      <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
        {rating > 0 ? (
          <>
            <div style={{ display:'flex', gap:1 }}>
              {[1,2,3,4,5].map(i => (
                <div key={i} style={{ width:10, height:10, borderRadius:2,
                  background: i <= rating ? 'var(--yellow)' : 'var(--border)' }}/>
              ))}
            </div>
            <span style={{ fontSize:10, color:'var(--text-dim)' }}>{rating}/5{lead.livello ? ` · ${lead.livello}` : ''}</span>
          </>
        ) : <span style={{ color:'var(--text-dim)', fontSize:11 }}>—</span>}
      </div>
      <StatoSelect stato={lead.stato} onChange={v => onStatoChange(lead.id, v)}/>
      <div style={{ display:'flex', gap:4, justifyContent:'flex-end' }}>
        <button onClick={() => onEdit(lead)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:13, padding:4 }}>✎</button>
        <button onClick={() => onDelete(lead.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:13, padding:4 }}>✕</button>
      </div>
    </div>
  );
}

/* ── TAB: Manuali ───────────────────────────────────────────────────── */
function TabManuali({ leads, utenti, onStatoChange, onEdit, onDelete, isAdmin }) {
  const [filtro, setFiltro] = useState('');
  const filtered = filtro ? leads.filter(l => l.stato === filtro) : leads;

  return (
    <div>
      <p style={{ margin:'0 0 14px', fontSize:13, color:'var(--text-muted)' }}>
        <b style={{ color:'var(--cyan)' }}>Flow</b> — contatti diretti, prospect e lead manuali
      </p>
      <FiltriStato leads={leads} filtro={filtro} onChange={setFiltro}/>
      {filtered.length === 0 ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:14 }}>
          Nessun lead manuale — usa "+ Nuovo lead" per aggiungerne uno
        </div>
      ) : (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <div className="eden-grid-head" style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr 1fr 1fr 1fr 80px',
              padding:'9px 16px', background:'var(--bg3)', borderBottom:'1px solid var(--border)', minWidth:600 }}>
              {['Nome / Azienda','Email','Telefono','Fonte','Stato',''].map((h,i) => (
                <span key={i} style={{ fontSize:10.5, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</span>
              ))}
            </div>
            {filtered.map(l => (
              <ManualeRow key={l.id} lead={l} utenti={utenti}
                onStatoChange={onStatoChange} onEdit={onEdit} onDelete={onDelete}/>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ManualeRow({ lead, onStatoChange, onEdit, onDelete }) {
  return (
    <div className="eden-grid-row" style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr 1fr 1fr 1fr 80px',
      alignItems:'center', padding:'10px 16px', borderBottom:'1px solid var(--border)', minWidth:600 }}>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.nome}</div>
        {lead.azienda && <div style={{ fontSize:11, color:'var(--text-muted)' }}>{lead.azienda}</div>}
      </div>
      <span style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.email || '—'}</span>
      <span style={{ fontSize:12, color:'var(--text-muted)' }}>{lead.telefono || '—'}</span>
      <span style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.fonte || '—'}</span>
      <StatoSelect stato={lead.stato} onChange={v => onStatoChange(lead.id, v)}/>
      <div style={{ display:'flex', gap:4, justifyContent:'flex-end' }}>
        <button onClick={() => onEdit(lead)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:13, padding:4 }}>✎</button>
        <button onClick={() => onDelete(lead.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:13, padding:4 }}>✕</button>
      </div>
    </div>
  );
}

/* ── helper ─────────────────────────────────────────────────────────── */
function MiniDetail({ label, value, children, full }) {
  return (
    <div style={{ minWidth: full ? '100%' : 100 }}>
      <div style={{ fontSize:9.5, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-dim)', marginBottom:2 }}>{label}</div>
      {children || <div style={{ fontSize:12, color:'var(--text-muted)' }}>{value}</div>}
    </div>
  );
}

/* ── componente principale ──────────────────────────────────────────── */
export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [lhStats, setLhStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState('lh'); // 'lh' | 'flow' | 'direttori'

  const isAdmin = user?.ruolo === 'admin' || user?.ruolo === 'boss';

  const load = useCallback(async () => {
    try { const l = await api.get('/leads'); setLeads(l.data); } catch {}
    try { const u = await api.get('/admin/utenti', { silent: true }); setUtenti(u.data); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    api.get('/leads/lh_stats').then(r => setLhStats(r.data)).catch(() => {});
  }, [load]);

  /* partition */
  const leadsLH        = leads.filter(l => l.fonte === 'lead-hunter');
  const leadsDirettori = leads.filter(l => l.direttore != null && l.fonte !== 'lead-hunter');
  const leadsManuali   = leads.filter(l => l.direttore == null && l.fonte !== 'lead-hunter');

  const submit = async () => {
    if (!form.nome.trim()) return;
    try {
      if (editId) {
        const r = await api.put(`/leads/${editId}`, form);
        setLeads(prev => prev.map(l => l.id === editId ? r.data : l));
      } else {
        const r = await api.post('/leads', form);
        setLeads(prev => [r.data, ...prev]);
      }
      setShowForm(false); setEditId(null); setForm(EMPTY);
    } catch(e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Eliminare questo lead?')) return;
    await api.delete(`/leads/${id}`);
    setLeads(prev => prev.filter(l => l.id !== id));
  };

  const handleStatoChange = async (id, nuovoStato) => {
    const lead = leads.find(l => l.id === id);
    if (!lead) return;
    try {
      const r = await api.put(`/leads/${id}`, {
        nome: lead.nome, azienda: lead.azienda||'', email: lead.email||'',
        telefono: lead.telefono||'', fonte: lead.fonte||'',
        stato: nuovoStato, note: lead.note||'', assegnato_a: lead.assegnato_a||'',
      });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, ...r.data } : l));
    } catch {}
  };

  const startEdit = (lead) => {
    setEditId(lead.id);
    setForm({
      nome: lead.nome||'', azienda: lead.azienda||'', email: lead.email||'',
      telefono: lead.telefono||'', fonte: lead.fonte||'', stato: lead.stato||'nuovo',
      note: lead.note||'', assegnato_a: lead.assegnato_a||'',
    });
    setShowForm(true);
  };

  const TABS = [
    {
      key:'lh', label:'Hunter', count: leadsLH.length, color:'var(--purple)',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/>
          <line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/>
          <line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/>
        </svg>
      ),
    },
    {
      key:'flow', label:'Flow', count: leadsManuali.length, color:'var(--cyan)',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="5" cy="6" r="2"/><circle cx="19" cy="6" r="2"/><circle cx="12" cy="18" r="2"/>
          <path d="M5 8v2a4 4 0 0 0 4 4h6a4 4 0 0 0 4-4V8"/>
          <line x1="12" y1="14" x2="12" y2="16"/>
        </svg>
      ),
    },
    {
      key:'direttori', label:'Direttori', count: leadsDirettori.length, color:'var(--yellow)',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="eden-page">

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:22, flexWrap:'wrap', gap:10 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, margin:'0 0 4px', color:'var(--text)' }}>Leads</h1>
          <p style={{ fontSize:13, color:'var(--text-muted)', margin:0 }}>
            {leads.length} totali · {leadsLH.length} EdenHunter · {leadsManuali.length} EdenFlow · {leadsDirettori.length} Direttori commerciali
          </p>
        </div>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY); setTab('flow'); }}
          className="eden-btn-primary" style={{ fontSize:12 }}>
          <Plus size={14}/> Nuovo lead
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ display:'flex', gap:0, marginBottom:22, borderBottom:'1px solid var(--border)' }}>
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                background: active ? `${t.color}0d` : 'none',
                border:'none', cursor:'pointer',
                padding:'10px 18px 12px',
                fontSize:13, fontWeight: active ? 600 : 400,
                color: active ? t.color : 'var(--text-muted)',
                borderBottom: active ? `2px solid ${t.color}` : '2px solid transparent',
                borderRadius: active ? '8px 8px 0 0' : 0,
                marginBottom:-1, transition:'color 0.15s, background 0.15s',
                display:'flex', alignItems:'center', gap:7,
              }}>
              <span style={{ opacity: active ? 1 : 0.55, color: active ? t.color : 'inherit' }}>
                {t.icon}
              </span>
              {t.label}
              <span style={{
                fontSize:10.5, padding:'1px 7px', borderRadius:10, fontWeight:700,
                background: active ? `${t.color}22` : 'var(--surface)',
                color: active ? t.color : 'var(--text-dim)',
              }}>
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Form crea/modifica */}
      {showForm && (
        <div style={{ background:'var(--surface)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <span style={{ fontSize:14, fontWeight:600, color:'var(--text)' }}>{editId ? 'Modifica lead' : 'Nuovo lead manuale'}</span>
            <button onClick={() => { setShowForm(false); setEditId(null); }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:4 }}>
              <X size={16}/>
            </button>
          </div>
          <div className="form-grid-3" style={{ marginBottom:10 }}>
            <input placeholder="Nome *" value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} className="eden-input"/>
            <input placeholder="Azienda" value={form.azienda} onChange={e=>setForm(f=>({...f,azienda:e.target.value}))} className="eden-input"/>
            <input placeholder="Email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} className="eden-input"/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:10, marginBottom:10 }}>
            <input placeholder="Telefono" value={form.telefono} onChange={e=>setForm(f=>({...f,telefono:e.target.value}))} className="eden-input"/>
            <input placeholder="Fonte" value={form.fonte} onChange={e=>setForm(f=>({...f,fonte:e.target.value}))} className="eden-input"/>
            <select value={form.stato} onChange={e=>setForm(f=>({...f,stato:e.target.value}))} className="eden-input">
              {STATI.map(s=><option key={s} value={s}>{STATO_STYLE[s].label}</option>)}
            </select>
            {utenti.length > 0 && (
              <select value={form.assegnato_a} onChange={e=>setForm(f=>({...f,assegnato_a:e.target.value}))} className="eden-input">
                <option value="">Assegna a…</option>
                {utenti.map(u=><option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>)}
              </select>
            )}
          </div>
          <textarea placeholder="Note" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} className="eden-input" style={{ minHeight:60, resize:'vertical', marginBottom:10 }}/>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={submit} className="eden-btn-primary" style={{ fontSize:12 }}>Salva</button>
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text-muted)', cursor:'pointer', padding:'7px 14px', fontSize:12 }}>
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Contenuto tab */}
      {loading ? (
        <ListSkeleton rows={6} />
      ) : tab === 'lh' ? (
        <TabLH lhStats={lhStats} />
      ) : tab === 'flow' ? (
        <TabManuali
          leads={leadsManuali} utenti={utenti} onStatoChange={handleStatoChange}
          onEdit={startEdit} onDelete={deleteLead} isAdmin={isAdmin}
        />
      ) : (
        <TabDirettori
          leads={leadsDirettori} onStatoChange={handleStatoChange} onEdit={startEdit} onDelete={deleteLead}
        />
      )}
    </div>
  );
}
